import { z } from "zod";

/**
 * The okthx-owned, connectorless calendars. Shared across BE (provisioning,
 * exclusion guards) and FE (calendar-picker rules) so both sides agree on which
 * calendars are okthx-owned. Future kinds (e.g. suggested events, travel
 * buffers) get their own entry here.
 *
 * "Meetings" holds commitments okthx arranged for an account that cannot write
 * to a provider calendar. It is separate from "Reminders" because the names are
 * user-facing and the two are not the same sort of thing — one is a prompt at a
 * moment, the other occupies its time.
 */
export type OkthxCalendarKind = "reminders" | "meetings";

export interface OkthxCalendarDef {
  /**
   * Reserved, stable calendarId. Convention: `okthx-<kind>`. Persisted on the
   * CalendarSource ent and in calendar preferences, so it must never change.
   */
  readonly id: string;
  /** User-facing calendar name. Not user-renamable. */
  readonly name: string;
  /** Color on first provisioning; user-changeable via the calendar-color endpoint. */
  readonly defaultColor: string;
}

export const OKTHX_CALENDARS: Record<OkthxCalendarKind, OkthxCalendarDef> = {
  reminders: { id: "okthx-reminders", name: "Reminders", defaultColor: "#8E44AD" },
  meetings: { id: "okthx-meetings", name: "Meetings", defaultColor: "#2E86C1" },
};

const OKTHX_CALENDAR_KIND_BY_ID: ReadonlyMap<string, OkthxCalendarKind> = new Map(
  (Object.entries(OKTHX_CALENDARS) as [OkthxCalendarKind, OkthxCalendarDef][]).map(
    ([kind, def]) => [def.id, kind],
  ),
);

/** The okthx calendar kind for a calendarId, or undefined if it isn't okthx-owned. */
export function getOkthxCalendarKind(calendarId: string): OkthxCalendarKind | undefined {
  return OKTHX_CALENDAR_KIND_BY_ID.get(calendarId);
}

/** True if `calendarId` belongs to any okthx-owned calendar. */
export function isOkthxCalendarId(calendarId: string): boolean {
  return OKTHX_CALENDAR_KIND_BY_ID.has(calendarId);
}

/**
 * The ways a fired reminder can reach someone. `app` is a push that opens the
 * reminder's card; the rest are the messaging channels of the same name.
 */
export const reminderChannels = ["text", "email", "whatsapp", "app"] as const;
export type ReminderChannel = (typeof reminderChannels)[number];

/**
 * Statsig gate over reminder channels beyond text and email and the
 * `notifications.reminders` channel toggles. Closed, reminders go by text or
 * email only, defaulting to text.
 */
export const REMINDER_CHANNELS_GATE = "reminder-channels";

/**
 * The okthx extension carried by an okthx-owned event. Lives in shared types
 * because both the stored source blob and the client-facing `CalendarEvent`
 * carry it, and a second declaration would be free to drift from the first.
 */
export const OkthxSourceBlobSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("reminder"),
    /** Delivery channel for the fired reminder. */
    remindBy: z.enum(reminderChannels),
  }),
  z.object({
    /**
     * A real commitment the user agreed to, held on the okthx calendar because
     * no connected calendar can hold it. Unlike a reminder, it occupies the
     * time — see `okthxKindBlocks`.
     */
    kind: z.literal("meeting"),
    /**
     * The UID of the `.ics` sent to participants, and the key that ties their
     * replies back to this event.
     *
     * Stored rather than derived because an inbound `METHOD:REPLY` cites it
     * verbatim and arrives with nothing else to match on. It must be chosen
     * when the event is written: a UID minted later cannot match the
     * invitations already sent.
     */
    icsUid: z.string(),
  }),
]);
export type OkthxSourceBlob = z.infer<typeof OkthxSourceBlobSchema>;
export type OkthxKind = OkthxSourceBlob["kind"];

export const CalendarEvent = z.strictObject({
  id: z.number(),
  eventId: z.string(),
  summary: z.string(),
  timeZone: z.string(),
  startTime: z.string(),
  endTime: z.string(),
});

export type CalendarEvent = z.infer<typeof CalendarEvent>;

/**
 * Set the user-changeable color of a calendar. Only the okthx (reminders)
 * calendar accepts this — provider calendar colors are read-only. Color is a
 * hex string (e.g. "#8E44AD").
 */
export const setCalendarColorReq = z.object({
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "color must be a 6-digit hex string"),
});
export type SetCalendarColorReq = z.infer<typeof setCalendarColorReq>;

/**
 * Change the channel a reminder fires on. Reminder-only, with no provider
 * equivalent, so it gets its own endpoint rather than riding the generic event
 * update — see `canChangeReminderChannel` for when it is refused.
 */
export const setReminderChannelReq = z.object({
  remindBy: z.enum(reminderChannels),
  /** Which occurrences of a repeating reminder to change. Defaults to just this one. */
  editScope: z.enum(["thisEvent", "thisAndFollowing", "allEvents"]).optional(),
});
export type SetReminderChannelReq = z.infer<typeof setReminderChannelReq>;

/** `GET /reminders/channels`: the channels a reminder to the user can go out on now, in display order. */
export type GetReminderChannelsResp = { channels: ReminderChannel[] };

export const syncDeviceCalendarItem = z.object({
  id: z.string(),
  title: z.string(),
  color: z.string().optional(),
  isPrimary: z.boolean().optional(),
});

export const syncDeviceCalendarEventItem = z.object({
  id: z.string(),
  calendarId: z.string(),
  title: z.string().optional(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  allDay: z.boolean(),
  location: z.string().optional(),
  notes: z.string().optional(),
  timeZone: z.string().optional(),
  url: z.string().optional(),
  // The EventKit event identifier (shared across instances of a recurring event).
  // Older native clients omit this; only the new instance-keyed uid path sends it.
  recurringEventId: z.string().optional(),
});

/** A little over the client's own -3mo…+1yr window, so a legitimate run always
 *  fits and a runaway one cannot retire an account's whole history. */
const MAX_RECONCILE_WINDOW_MS = 550 * 24 * 60 * 60 * 1000;

/**
 * Closes out a sync run: everything this device holds for the window has now
 * been uploaded, so anything inside it not carrying this run's
 * `syncGeneration` is gone from the phone and should stop being live here.
 *
 * Sent only on the run's last batch. A run that dies partway never sends it, so
 * a failed upload cancels nothing — the reason completeness is declared
 * explicitly rather than inferred from a short final batch, which an aborted
 * run also produces.
 */
export const syncDeviceCalendarReconcile = z
  .object({
    windowStart: z.string().datetime(),
    windowEnd: z.string().datetime(),
  })
  // The window drives a bulk cancel, so it is the one client-supplied value
  // worth bounding: unchecked, a single request spanning year 1 to year 9999
  // retires every device event the account has and queues a re-extraction job
  // for each one.
  .refine((w) => new Date(w.windowStart) < new Date(w.windowEnd), {
    message: "windowStart must be before windowEnd",
  })
  .refine(
    (w) =>
      new Date(w.windowEnd).getTime() - new Date(w.windowStart).getTime() <=
      MAX_RECONCILE_WINDOW_MS,
    { message: "reconcile window is too wide" },
  );

export const syncDeviceCalendarEventsReq = z.object({
  deviceName: z.string().max(200).optional(),
  /** Stable per-install id. Absent from clients that predate the split, which
   *  keeps them writing to the legacy-addressed connector. */
  installId: z.string().min(1).max(128).optional(),
  calendars: z.array(syncDeviceCalendarItem).max(100),
  events: z.array(syncDeviceCalendarEventItem).max(5000),
  /**
   * Opaque token identifying one sync run, repeated across its batches.
   * Optional: clients that predate reconciliation keep upserting without it and
   * simply never cancel anything.
   */
  syncGeneration: z.string().min(1).max(64).optional(),
  reconcile: syncDeviceCalendarReconcile.optional(),
});
